// Installation Process — full customer-facing page.
import { Link } from "react-router-dom";
import {
  ArrowRight, ClipboardCheck, MapPin, Sparkles, FileText, CalendarCheck,
  Wrench, ShieldCheck, Home, Droplets, Zap, Building2, Trees, Layers,
  CircuitBoard, CheckCircle2, AlertTriangle, PhoneCall, Calculator as CalcIcon,
  Handshake, ExternalLink,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";

const PARTNER_REGIONS = [
  {
    region: "Canterbury",
    partners: [
      {
        name: "Simply Plumbing",
        logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/ivbprdjz_Simply-plumbing-logo-.png",
        url: "https://simplyplumbing.co.nz/",
        trade: "Plumbing",
        dark: false,
      },
      {
        name: "BDF Electrical Ltd",
        logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/yvyfev8o_BDF%20Logo.webp",
        url: "https://www.bdfelectrical.co.nz/",
        trade: "Electrical",
        dark: false,
      },
    ],
  },
  {
    region: "Manawatū",
    partners: [
      {
        name: "Fast Flow Plumbing",
        logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/yj8iolfb_Fast_Flow_Plumbing_logo.webp",
        url: "https://fastflow.co.nz/",
        trade: "Plumbing",
        dark: true,
      },
      {
        name: "BDF Electrical Ltd",
        logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/yvyfev8o_BDF%20Logo.webp",
        url: "https://www.bdfelectrical.co.nz/",
        trade: "Electrical",
        dark: false,
      },
    ],
  },
];

const STEPS = [
  { n: "01", icon: ClipboardCheck, t: "Free assessment",          b: "Tell us about your current hot water setup. We'll review remotely first to keep things simple." },
  { n: "02", icon: MapPin,         t: "Site suitability check",   b: "We confirm the right install location, access, and any site-specific factors." },
  { n: "03", icon: Sparkles,       t: "Product recommendation",   b: "We recommend the most suitable AeroMax system for your household demand and home." },
  { n: "04", icon: FileText,       t: "Quote confirmation",       b: "A clear, transparent quote — including any plumbing, electrical or access work required." },
  { n: "05", icon: CalendarCheck,  t: "Installation booking",     b: "We coordinate a time that suits you and your installer partner." },
  { n: "06", icon: Wrench,         t: "Professional install & support", b: "Tidy install, proper commissioning, and customer handover with ongoing AeroMax support." },
];

const CHECKS = [
  { icon: Home,         t: "Current hot water system",        b: "Gas, electric, LPG or legacy — we confirm what's being replaced." },
  { icon: Droplets,     t: "Household hot water demand",      b: "Sizing for your bathrooms, residents and daily usage profile." },
  { icon: MapPin,       t: "Suitable installation location",  b: "A clean, ventilated spot that meets product clearance requirements." },
  { icon: Layers,       t: "Plumbing access",                 b: "Pipe routing, valves and any reconfiguration needs." },
  { icon: Zap,          t: "Electrical requirements",         b: "Switchboard headroom, circuit capacity, RCD/breaker compliance." },
  { icon: CircuitBoard, t: "Drainage & ventilation",          b: "Condensate drain, airflow and outdoor/indoor placement." },
  { icon: Building2,    t: "Access around the property",      b: "Pathways, gates, and clearance for a safe install." },
  { icon: Trees,        t: "Site-specific factors",           b: "Microclimate, neighbours, weatherboard penetrations and noise positioning." },
];

function Hero() {
  return (
    <section data-testid="install-hero" className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Wrench className="h-3 w-3 text-[#10B981]" /> Installation Process
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Professional installation, <span className="text-[#10B981]">made simple.</span>
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            AeroMax helps make the move to efficient hot water heat pump technology clear, supported, and straightforward — from first assessment to final installation.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/free-home-assessment" data-testid="install-cta-assess" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
              Request Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/savings-calculator" data-testid="install-cta-calc" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
              Calculate Your Savings
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section data-testid="install-journey" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">The journey</span>
          <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            How the installation <span className="text-[#10B981]">journey works.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Six clear steps — designed to be transparent, low-friction and supported all the way through.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="group h-full rounded-2xl bg-white border border-[#E2E8F0] p-6 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20">
                    <s.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="font-heading text-2xl font-bold text-[#E2E8F0] num-tab leading-none">{s.n}</span>
                </div>
                <h3 className="font-heading mt-5 text-lg font-bold text-[#0B132B] leading-snug">{s.t}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PreInstallChecks() {
  return (
    <section data-testid="install-checks" className="py-20 sm:py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Pre-install checks</span>
          <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            What we check <span className="text-[#10B981]">before installation.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Each home is different — we work through these so there are no surprises on install day.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CHECKS.map((c, i) => (
            <Reveal key={i} delay={i * 50}>
              <div className="h-full rounded-2xl bg-white border border-[#E2E8F0] p-5 hover:border-[#10B981]/30 hover:shadow-md transition-all">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B132B] text-white">
                  <c.icon className="h-4 w-4" strokeWidth={2.2} />
                </span>
                <p className="font-heading mt-4 text-sm font-bold text-[#0B132B] leading-snug">{c.t}</p>
                <p className="mt-1.5 text-xs text-[#475569] leading-relaxed">{c.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SwapVsCustom() {
  return (
    <section data-testid="install-swap" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Two pathways</span>
          <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            Straight swap or <span className="text-[#10B981]">customised install?</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="rounded-3xl bg-gradient-to-br from-white to-[#F0FDF4] border border-[#10B981]/25 p-7 sm:p-9 shadow-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/15 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
                <Wrench className="h-3 w-3" /> Straight swap
              </span>
              <h3 className="font-heading mt-3 text-2xl font-bold text-[#0B132B] leading-tight">
                Direct replacement
              </h3>
              <p className="mt-3 text-[#475569] leading-relaxed">
                When the existing location, plumbing and electrical supply already suit a NeoPower install, we can typically deliver a clean, low-disruption replacement of the old system.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {["Same general location", "Compatible plumbing", "Suitable electrical supply"].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#0B132B]">
                    <CheckCircle2 className="h-4 w-4 text-[#10B981]" strokeWidth={2.4} /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-3xl bg-[#0B132B] text-white p-7 sm:p-9 shadow-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/12 backdrop-blur text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                <Layers className="h-3 w-3 text-[#10B981]" /> Customised install
              </span>
              <h3 className="font-heading mt-3 text-2xl font-bold leading-tight">
                Some additional work
              </h3>
              <p className="mt-3 text-white/75 leading-relaxed">
                Other homes may need additional plumbing, electrical capacity, repositioning, drainage or access work — final pricing depends on what's confirmed at site assessment.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {["Relocation or repositioning", "Electrical upgrades", "Plumbing or drainage changes", "Access or structural adjustments"].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/85">
                    <ArrowRight className="h-4 w-4 text-[#10B981]" strokeWidth={2.4} /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PartnerNetwork() {
  return (
    <section data-testid="install-partners" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Our installation partner network</span>
          <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            Trusted local trades, <span className="text-[#10B981]">region by region.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            We install through a trusted network of certified local plumbers and electricians — ensuring every AeroMax NeoPower system is fitted to the highest standard, right in your region.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {PARTNER_REGIONS.map((r, i) => (
            <Reveal key={r.region} delay={i * 120}>
              <div
                data-testid={`partner-region-${r.region.toLowerCase()}`}
                className="h-full rounded-3xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] p-7 sm:p-8 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20">
                    <MapPin className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <h3 className="font-heading text-xl font-bold text-[#0B132B] leading-tight">{r.region}</h3>
                  <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0B132B]/[0.04] text-[#475569] text-[10px] font-semibold tracking-[0.14em] uppercase">
                    <Handshake className="h-3 w-3 text-[#10B981]" /> {r.partners.length} partners
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {r.partners.map((p, j) => (
                    <a
                      key={`${r.region}-${p.name}-${j}`}
                      data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}-${r.region.toLowerCase()}`}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex flex-col items-center gap-3 rounded-2xl bg-white border border-[#E2E8F0] px-4 py-5 hover:border-[#10B981]/40 hover:shadow-md hover:-translate-y-0.5 transition-all"
                      aria-label={`Visit ${p.name} website (opens in new tab)`}
                    >
                      <span className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ExternalLink className="h-3.5 w-3.5 text-[#10B981]" strokeWidth={2.2} />
                      </span>
                      <div className={`flex h-16 w-full items-center justify-center rounded-xl transition-colors ${p.dark ? "bg-[#0B132B]" : ""}`}>
                        <img
                          src={p.logo}
                          alt={`${p.name} logo`}
                          loading="lazy"
                          className="max-h-14 max-w-[85%] object-contain"
                        />
                      </div>
                      <div className="text-center">
                        <p className="font-heading text-sm font-bold text-[#0B132B] leading-tight">{p.name}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#10B981]">{p.trade}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <p className="mt-8 text-xs text-[#475569] flex items-center gap-1.5 max-w-3xl">
            <ShieldCheck className="h-3.5 w-3.5 text-[#10B981] shrink-0" />
            Expanding installation support region by region — new partner trades added as coverage grows across New Zealand.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section data-testid="install-trust" className="py-16 sm:py-20 bg-[#F8FAFC]">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-[#E2E8F0] bg-white p-7 sm:p-9 shadow-sm flex items-start gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20 shrink-0">
              <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">No surprises</p>
              <p className="mt-2 text-lg text-[#0B132B] leading-relaxed">
                <strong className="font-bold">Every home is different.</strong> AeroMax provides estimates first, then confirms final suitability and installation requirements after assessment.
              </p>
              <p className="mt-2 text-xs text-[#475569] flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[#10B981]" /> Pricing is indicative only until site assessment is complete.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section data-testid="install-final-cta" className="relative py-20 sm:py-24 bg-[#0B132B] text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(50%_70%_at_50%_50%,rgba(16,185,129,0.18),transparent_60%)] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-[1.05]">
            Ready to plan your <span className="text-[#10B981]">hot water upgrade?</span>
          </h2>
          <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
            Start with a free assessment, or run the numbers yourself with our savings calculator.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/free-home-assessment" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
              <PhoneCall className="h-4 w-4" /> Request Free Assessment
            </Link>
            <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
              <CalcIcon className="h-4 w-4" /> Calculate Your Savings
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Installation() {
  return (
    <div className="bg-white">
      <Hero />
      <Journey />
      <PreInstallChecks />
      <SwapVsCustom />
      <PartnerNetwork />
      <Trust />
      <FinalCTA />
    </div>
  );
}
